import React, { useState, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { useUI } from '../hooks/useUI';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { motion, AnimatePresence } from 'framer-motion'; // Import motion and AnimatePresence

interface NotificationAction {
  label: string;
  onClick: () => void;
  primary?: boolean;
}

interface NotificationProps {
  notification: {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
    actions?: NotificationAction[];
  };
}

const Notification: React.FC<NotificationProps> = ({ notification }) => {
  const { clearNotification } = useNotifications();
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (notification.duration) {
      const interval = setInterval(() => {
        setProgress((prevProgress) =>
          Math.max(0, prevProgress - 100 / (notification.duration! / 100)),
        );
      }, 100);

      const timeout = setTimeout(() => {
        clearNotification(notification.id);
      }, notification.duration);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
    return () => {};
  }, [notification, clearNotification]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5" />;
      case 'error':
        return <AlertCircle className="h-5 w-5" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5" />;
      default:
        return <Info className="h-5 w-5" />;
    }
  };

  const getStyles = () => {
    const baseStyles =
      'p-4 rounded-lg shadow-lg flex items-center gap-3 relative overflow-hidden';

    let typeStyles = '';
    switch (notification.type) {
      case 'success':
        typeStyles = 'bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100';
        break;
      case 'error':
        typeStyles = 'bg-red-100 text-red-900 dark:bg-red-900 dark:text-red-100';
        break;
      case 'warning':
        typeStyles = 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900 dark:text-yellow-100';
        break;
      default:
        typeStyles = 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100';
    }
    return twMerge(baseStyles, typeStyles);
  };

  return (
    <motion.div // Use motion.div for animations
      id={notification.id}
      className={getStyles()}
      initial={{ opacity: 0, x: 0 }} // Initial state for enter animation (fade in from slightly below)
      animate={{ opacity: 1, x: 20 }} // Animate to visible state
      exit={{ opacity: 0, x: 0 }} // Animate to exit state (fade out to slightly below)
      transition={{ duration: 0.2, ease: "easeOut" }} // Animation duration and easing
    >
      {/* Timer Bar - only show for notifications with a positive duration */}
      {notification.duration && notification.duration > 0 && (
        <div
          className="absolute bottom-0 left-0 h-1 bg-black/10 dark:bg-white/10"
          style={{ width: `${progress}%`, transition: 'width 100ms linear' }}
        />
      )}

      <div className="flex items-center">
        {getIcon()}
      </div>
      <span className="text-sm font-medium">{notification.message}</span>
      
      {notification.actions && notification.actions.length > 0 ? (
        <div className="flex gap-2 ml-auto">
          {notification.actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick();
                clearNotification(notification.id);
              }}
              className={twMerge(
                "px-3 py-1 text-xs font-medium rounded transition-colors",
                action.primary 
                  ? "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600" 
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              )}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : (
        <button
          onClick={() => {
            clearNotification(notification.id);
          }}
          className="ml-auto p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </motion.div>
  );
};

const Notifications: React.FC = () => {
  const { notifications } = useNotifications();
  const { isEmbedMode } = useUI();

  if (isEmbedMode) {
    return null;
  }

  return (
    <div className="fixed bottom-16 left-16 z-50 space-y-4">
      <AnimatePresence> {/* Wrap with AnimatePresence */}
        {notifications.map((notification) => (
          <Notification key={notification.id} notification={notification} />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default Notifications;
